import dayjs from 'dayjs';

export type TimingTab = 'weekly' | 'monthly' | 'yearly';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Calculate date range based on selected date and timing tab
 * @param selectedDate The selected date (usually current date)
 * @param timingTab The timing tab (weekly, monthly, yearly)
 * @returns Object with startDate and endDate
 */
export const calculateDateRange = (selectedDate: Date, timingTab: TimingTab): DateRange => {
  const date = dayjs(selectedDate);

  switch (timingTab) {
    case 'weekly':
      // Get start of week (Monday) and end of week (Sunday)
      const startOfWeek = date.startOf('week').add(1, 'day'); // Monday
      const endOfWeek = date.endOf('week').add(1, 'day'); // Sunday
      return {
        startDate: startOfWeek.toDate(),
        endDate: endOfWeek.toDate()
      };

    case 'monthly':
      // Get start and end of month
      return {
        startDate: date.startOf('month').toDate(),
        endDate: date.endOf('month').toDate()
      };

    case 'yearly':
      // Get start and end of year
      return {
        startDate: date.startOf('year').toDate(),
        endDate: date.endOf('year').toDate()
      };

    default:
      return {
        startDate: date.toDate(),
        endDate: date.toDate()
      };
  }
};

/**
 * Calculate date range for trending data (typically shows historical data)
 * For trending, we usually want to show multiple periods leading up to the current period
 * @param selectedDate The selected date (usually current date)
 * @param timingTab The timing tab (weekly, monthly, yearly)
 * @param periodsCount Number of periods to show (default: 6 for monthly, 12 for weekly, 2 for yearly)
 * @returns Object with startDate and endDate
 */
export const calculateTrendingDateRange = (
  selectedDate: Date, 
  timingTab: TimingTab, 
  periodsCount?: number
): DateRange => {
  const date = dayjs(selectedDate);

  // Set default periods count based on timing tab
  const defaultPeriodsCount = periodsCount || (
    timingTab === 'weekly' ? 12 : 
    timingTab === 'monthly' ? 6 : 
    2 // yearly
  );

  switch (timingTab) {
    case 'weekly':
      // End of current week (Sunday)
      const endOfWeek = date.endOf('week').add(1, 'day'); // Sunday
      // Start of week N periods ago (Monday)
      const startOfWeek = endOfWeek.subtract(defaultPeriodsCount - 1, 'week').startOf('week').add(1, 'day'); // Monday
      return {
        startDate: startOfWeek.toDate(),
        endDate: endOfWeek.toDate()
      };

    case 'monthly':
      // End of current month
      const endOfMonth = date.endOf('month');
      // Start of month N periods ago
      const startOfMonth = endOfMonth.subtract(defaultPeriodsCount - 1, 'month').startOf('month');
      return {
        startDate: startOfMonth.toDate(),
        endDate: endOfMonth.toDate()
      };

    case 'yearly':
      // End of current year
      const endOfYear = date.endOf('year');
      // Start of year N periods ago
      const startOfYear = endOfYear.subtract(defaultPeriodsCount - 1, 'year').startOf('year');
      return {
        startDate: startOfYear.toDate(),
        endDate: endOfYear.toDate()
      };

    default:
      return {
        startDate: date.toDate(),
        endDate: date.toDate()
      };
  }
};

/**
 * Format date range for display
 * @param dateRange The date range to format
 * @param timingTab The timing tab for context
 * @returns Formatted string representation of the date range
 */
export const formatDateRange = (dateRange: DateRange, timingTab: TimingTab): string => {
  const { startDate, endDate } = dateRange;
  
  switch (timingTab) {
    case 'weekly':
      if (startDate.getMonth() === endDate.getMonth()) {
        return `${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('D, YYYY')}`;
      } else {
        return `${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('MMM D, YYYY')}`;
      }
    
    case 'monthly':
      return dayjs(startDate).format('MMMM YYYY');
    
    case 'yearly':
      return dayjs(startDate).format('YYYY');
    
    default:
      return `${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('MMM D, YYYY')}`;
  }
}; 